/**
 * 轻量代码高亮渲染器（零依赖）。
 *
 * 按扩展名映射到语言族（c-like / python / shell / css / markdown），
 * 每行用「注释状态机 + 交替正则」切成 token span：注释 / 字符串 / 数字 / 关键字。
 * 安全：全部走 textContent，不使用 innerHTML。
 * 超 5000 行只渲染前 5000 行（末尾提示），避免大文件卡死手机。
 */
import { h } from './ui';

/** token 颜色类名（样式见 index.html） */
type Tok = 'cmt' | 'str' | 'num' | 'kw';

const MAX_RENDER_LINES = 5000;

/* ------------------------------ 语言映射 ------------------------------ */

type Lang = 'ts' | 'js' | 'json' | 'py' | 'sh' | 'css' | 'go' | 'rs' | 'java' | 'clike' | 'plain';

const EXT_LANG: Record<string, Lang> = {
  ts: 'ts', tsx: 'ts', mts: 'ts', cts: 'ts',
  js: 'js', mjs: 'js', cjs: 'js', jsx: 'js',
  json: 'json',
  py: 'py', pyi: 'py',
  sh: 'sh', bash: 'sh', zsh: 'sh', ps1: 'sh',
  yml: 'sh', yaml: 'sh', toml: 'sh', ini: 'sh', conf: 'sh', env: 'sh', gitignore: 'sh', dockerfile: 'sh',
  css: 'css',
  go: 'go', rs: 'rs',
  java: 'java',
  c: 'clike', h: 'clike', cpp: 'clike', hpp: 'clike', cc: 'clike',
};

function langOf(filename: string): Lang {
  const base = filename.replace(/\\/g, '/').split('/').pop() ?? '';
  const ext = base.includes('.') ? base.split('.').pop()!.toLowerCase() : base.toLowerCase();
  if (ext === 'dockerfile') return 'sh';
  return EXT_LANG[ext] ?? 'plain';
}

/* ------------------------------ 关键字表 ------------------------------ */

const KW: Record<string, string[]> = {
  ts: ['const', 'let', 'var', 'function', 'return', 'if', 'else', 'for', 'while', 'do', 'switch', 'case', 'break', 'continue', 'new', 'class', 'extends', 'import', 'from', 'export', 'default', 'try', 'catch', 'finally', 'throw', 'async', 'await', 'yield', 'typeof', 'instanceof', 'delete', 'void', 'this', 'super', 'static', 'interface', 'type', 'enum', 'implements', 'namespace', 'declare', 'as', 'satisfies', 'readonly', 'public', 'private', 'protected', 'null', 'undefined', 'true', 'false'],
  js: ['const', 'let', 'var', 'function', 'return', 'if', 'else', 'for', 'while', 'do', 'switch', 'case', 'break', 'continue', 'new', 'class', 'extends', 'import', 'from', 'export', 'default', 'try', 'catch', 'finally', 'throw', 'async', 'await', 'yield', 'typeof', 'instanceof', 'delete', 'void', 'this', 'super', 'null', 'undefined', 'true', 'false'],
  json: ['true', 'false', 'null'],
  py: ['def', 'class', 'return', 'if', 'elif', 'else', 'for', 'while', 'import', 'from', 'as', 'try', 'except', 'finally', 'raise', 'with', 'lambda', 'pass', 'break', 'continue', 'global', 'nonlocal', 'yield', 'assert', 'del', 'in', 'is', 'not', 'and', 'or', 'None', 'True', 'False', 'async', 'await', 'self', 'match', 'case'],
  sh: ['if', 'then', 'else', 'elif', 'fi', 'for', 'do', 'done', 'while', 'until', 'case', 'esac', 'in', 'function', 'export', 'local', 'return', 'echo', 'exit', 'set', 'source', 'param'],
  css: ['media', 'import', 'keyframes', 'font-face', 'supports', 'important', 'root'],
  go: ['func', 'package', 'import', 'var', 'const', 'type', 'struct', 'interface', 'map', 'chan', 'go', 'defer', 'if', 'else', 'for', 'range', 'return', 'switch', 'case', 'break', 'continue', 'nil', 'true', 'false', 'string', 'int', 'error', 'make', 'new', 'select'],
  rs: ['fn', 'let', 'mut', 'const', 'struct', 'enum', 'impl', 'trait', 'use', 'pub', 'mod', 'match', 'if', 'else', 'for', 'while', 'loop', 'return', 'Some', 'None', 'Ok', 'Err', 'self', 'crate', 'as', 'ref', 'move', 'async', 'await', 'true', 'false'],
  java: ['public', 'private', 'protected', 'class', 'interface', 'extends', 'implements', 'static', 'final', 'void', 'int', 'long', 'double', 'float', 'boolean', 'char', 'new', 'return', 'if', 'else', 'for', 'while', 'do', 'switch', 'case', 'break', 'continue', 'try', 'catch', 'finally', 'throw', 'throws', 'import', 'package', 'this', 'super', 'null', 'true', 'false'],
  clike: ['int', 'long', 'short', 'char', 'float', 'double', 'void', 'unsigned', 'signed', 'const', 'static', 'struct', 'enum', 'union', 'typedef', 'class', 'public', 'private', 'protected', 'virtual', 'override', 'template', 'typename', 'namespace', 'using', 'new', 'delete', 'return', 'if', 'else', 'for', 'while', 'do', 'switch', 'case', 'break', 'continue', 'try', 'catch', 'throw', 'nullptr', 'NULL', 'true', 'false'],
  plain: [],
};

/** c-like 族（含 ts/js/json/go/rs/java/clike）的交替正则；块注释由状态机预处理 */
function clikeRe(lang: Lang): RegExp {
  const kws = KW[lang].join('|');
  return new RegExp(
    `("(?:\\\\.|[^"\\\\])*"|'(?:\\\\.|[^'\\\\])*'|\`(?:\\\\.|[^\`\\\\])*\`)` + // 字符串
    `|(\\/\\/.*$)` +                                                            // 行注释
    `(\\b0x[0-9a-fA-F]+\\b|\\b\\d[\\d_]*(?:\\.\\d+)?(?:[eE][+-]?\\d+)?\\b)` +  // 数字
    (kws ? `|\\b(${kws})\\b` : ''),                                             // 关键字
    'g',
  );
}

/* ------------------------------ 单行扫描 ------------------------------ */

interface ScanState {
  /** 处于 /* 块注释中（c-like / css） */
  block: boolean;
  /** 处于 python 三引号字符串中 */
  triple: string; // '' | '"""' | "'''"
}

interface Piece {
  cls: Tok | '';
  text: string;
}

function scanLine(line: string, lang: Lang, st: ScanState): Piece[] {
  const out: Piece[] = [];
  let pos = 0;
  const push = (cls: Tok | '', text: string): void => {
    if (!text) return;
    const last = out[out.length - 1];
    if (last && last.cls === cls) last.text += text;
    else out.push({ cls, text });
  };

  if (lang === 'plain') return [{ cls: '', text: line }];

  while (pos < line.length) {
    // 跨行状态：块注释内找 */
    if (st.block) {
      const end = line.indexOf('*/', pos);
      if (end === -1) {
        push('cmt', line.slice(pos));
        pos = line.length;
      } else {
        push('cmt', line.slice(pos, end + 2));
        pos = end + 2;
        st.block = false;
      }
      continue;
    }
    // 跨行状态：python 三引号内找闭合
    if (st.triple) {
      const end = line.indexOf(st.triple, pos);
      if (end === -1) {
        push('str', line.slice(pos));
        pos = line.length;
      } else {
        push('str', line.slice(pos, end + 3));
        pos = end + 3;
        st.triple = '';
      }
      continue;
    }

    const rest = line.slice(pos);

    // python：行注释与三引号开场
    if (lang === 'py') {
      const hash = rest.indexOf('#');
      const tq = rest.match(/("""|''')/);
      if (hash !== -1 && (tq === null || hash < tq.index!)) {
        push('cmt', rest.slice(hash));
        break;
      }
      if (tq) {
        if (tq.index! > 0) scanPlain(rest.slice(0, tq.index!), lang, push);
        const open = tq[1];
        // 同行找闭合
        const close = rest.indexOf(open, tq.index! + 3);
        if (close === -1) {
          push('str', rest.slice(tq.index!));
          st.triple = open;
          break;
        }
        push('str', rest.slice(tq.index!, close + 3));
        pos += close + 3;
        continue;
      }
      scanPlain(rest, lang, push);
      break;
    }

    // shell 族：# 行注释（不能误伤 $# 等；从行首剥离空白后判断即可）
    if (lang === 'sh') {
      const hash = rest.indexOf('#');
      if (hash !== -1 && /^\s*$/.test(rest.slice(0, hash))) {
        push('', rest.slice(0, hash));
        push('cmt', rest.slice(hash));
        break;
      }
      scanPlain(rest, lang, push);
      break;
    }

    // c-like / css：块注释开场优先于其它 token
    if (lang !== 'json') {
      const bs = rest.indexOf('/*');
      if (bs !== -1) {
        // 先渲染开场前的普通片段（其中可能含字符串/关键字）
        if (bs > 0) {
          const head = rest.slice(0, bs);
          if (lang === 'css') scanCss(head, push);
          else scanPlain(head, lang, push);
        }
        const close = rest.indexOf('*/', bs + 2);
        if (close === -1) {
          push('cmt', rest.slice(bs));
          st.block = true;
          break;
        }
        push('cmt', rest.slice(bs, close + 2));
        pos += close + 2;
        continue;
      }
    }

    if (lang === 'css') {
      scanCss(rest, push);
      break;
    }
    scanPlain(rest, lang, push);
    break;
  }
  return out;
}

/** 交替正则扫描一段无注释文本（c-like 族） */
function scanPlain(seg: string, lang: Lang, push: (cls: Tok | '', text: string) => void): void {
  const re = clikeRe(lang);
  re.lastIndex = 0;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(seg)) !== null) {
    if (m.index > last) push('', seg.slice(last, m.index));
    // json 没有行注释分组语义差异：按捕获组判断类型
    if (m[1] !== undefined) push('str', m[0]);
    else if (m[2] !== undefined) push('cmt', m[0]);
    else if (m[3] !== undefined) push('num', m[0]);
    else push('kw', m[0]);
    last = m.index + m[0].length;
  }
  if (last < seg.length) push('', seg.slice(last));
}

/** css：字符串 / 数字(带单位) / @关键字 / 属性名: */
function scanCss(seg: string, push: (cls: Tok | '', text: string) => void): void {
  const re = /("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*')|(-?\d+(?:\.\d+)?(?:px|em|rem|vh|vw|s|ms|%|fr|deg)?)|(@[\w-]+)|\b([\w-]+)(?=\s*:)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(seg)) !== null) {
    if (m.index > last) push('', seg.slice(last, m.index));
    if (m[1] !== undefined) push('str', m[0]);
    else if (m[2] !== undefined) push('num', m[0]);
    else if (m[3] !== undefined) push('kw', m[0]);
    else push('kw', m[0]);
    last = m.index + m[0].length;
  }
  if (last < seg.length) push('', seg.slice(last));
}

/* ------------------------------ 主入口 ------------------------------ */

/**
 * 把代码文本渲染为带行号的 DOM 容器。
 * 超过 MAX_RENDER_LINES 行只渲染前面部分，末尾给提示行。
 */
export function renderCode(text: string, filename: string): HTMLElement {
  const lang = langOf(filename);
  const box = h('div', { class: 'codeview' });
  const lines = text.split('\n');
  const st: ScanState = { block: false, triple: '' };
  const n = Math.min(lines.length, MAX_RENDER_LINES);

  for (let i = 0; i < n; i++) {
    const lineEl = h('div', { class: 'codeline' },
      h('span', { class: 'lineno', text: String(i + 1) }));
    const codeEl = h('span', { class: 'codetext' });
    if (lang === 'plain' || lines[i] === '') {
      codeEl.textContent = lines[i];
    } else {
      for (const p of scanLine(lines[i], lang, st)) {
        if (p.cls) codeEl.append(h('span', { class: `tok-${p.cls}`, text: p.text }));
        else codeEl.append(document.createTextNode(p.text));
      }
    }
    lineEl.append(codeEl);
    box.append(lineEl);
  }
  if (lines.length > n) {
    box.append(h('div', { class: 'codeline' },
      h('span', { class: 'lineno', text: '…' }),
      h('span', { class: 'codetext muted', text: `共 ${lines.length} 行，仅渲染前 ${n} 行` })));
  }
  return box;
}
